/**
 * Mine — what happened to what I submitted.
 *
 * The promise of a suggestion box that doesn't lie: every submission shows its
 * fate, in plain language, sourced from the canonical triage assessment.
 */

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useTriageStore } from '@/stores/prioritization/triageStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import type { TriageOutcome } from '@/types/prioritization/triage';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, PrimaryButton, StatusChip } from '../components/primitives';

/** Plain-language fate, so a submitter never has to decode lifecycle jargon. */
const OUTCOME_COPY: Record<TriageOutcome, { label: string; tone: 'green' | 'mustard' | 'coral' | 'slate' }> = {
  pursue: { label: 'Being pursued', tone: 'green' },
  defer: { label: 'Set aside for now', tone: 'mustard' },
  reject: { label: 'Not going ahead', tone: 'coral' },
  needs_clarification: { label: 'Needs a bit more from you', tone: 'mustard' },
  redirect: { label: 'Sent somewhere better', tone: 'slate' },
};

export default function MySubmissionsScreen() {
  const app = getAppDefinition('intake')!;
  const navigate = useNavigate();

  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const assessments = useTriageStore((s) => s.assessments);
  const fetchTriage = useTriageStore((s) => s.fetchFromSupabase);
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);

  useEffect(() => {
    void fetchItems();
    void fetchTriage();
  }, [fetchItems, fetchTriage]);

  const mine = useMemo(() => {
    const me = new Set([userId, userName].filter(Boolean) as string[]);
    return items
      .filter((i) => (i.createdBy && me.has(i.createdBy)) || i.ownerId === userId)
      .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
  }, [items, userId, userName]);

  return (
    <AppShell
      app={app}
      backTo="/apps/intake"
      backLabel="Back to drop"
      actions={
        <PrimaryButton onClick={() => navigate('/apps/intake')} className="!px-3 !py-1.5">
          <Plus className="h-3.5 w-3.5" /> Drop
        </PrimaryButton>
      }
    >
      <ScreenHeading
        title="What you sent in"
        subtitle="Every submission and what happened to it — including the ones that didn't go ahead."
      />
      <BallLine ballWith="the triagers — they decide what happens next" />

      {mine.length === 0 ? (
        <EmptyState
          title="You haven't sent anything in yet"
          hint="Drop an idea and you'll be able to follow it here."
          action={<PrimaryButton onClick={() => navigate('/apps/intake')}>Drop an idea</PrimaryButton>}
        />
      ) : (
        <div className="space-y-2">
          {mine.map((item) => {
            const assessment = assessments?.[item.id];
            const copy = assessment ? OUTCOME_COPY[assessment.outcome] : null;
            return (
              <AppCard key={item.id} className="!p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                      {item.title}
                    </p>
                    {assessment?.rationale ? (
                      <p className="mt-1 font-body text-xs text-gray-600 dark:text-[var(--text-sec)]">
                        “{assessment.rationale}”
                      </p>
                    ) : (
                      <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                        {assessment ? 'Decided.' : 'Waiting to be sorted.'}
                      </p>
                    )}
                  </div>
                  <StatusChip
                    label={copy?.label ?? 'In the queue'}
                    tone={copy?.tone ?? 'neutral'}
                  />
                </div>
              </AppCard>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
